import { createFileRoute } from '@tanstack/react-router'
import { ComparisonPage, comparisonScripts, type ComparisonData } from '@/components/comparison-page'
import { MONITOR_LOCATIONS } from '@/lib/check-locations'

export const Route = createFileRoute('/_site/compare/updown')({
  component: () => <ComparisonPage data={data} />,
  head: () => ({
    meta: [
      { title: 'Updown.io Alternative — Uptrack Uptime Monitoring' },
      { name: 'description', content: "Uptrack vs Updown.io: pay-per-check credits vs flat plans. Compare check intervals, locations, status pages, and team access." },
      { property: 'og:title', content: 'Updown.io Alternative — Uptrack' },
      { property: 'og:description', content: "Updown.io bills per check with prepaid credits and supports 15s checks. Uptrack has flat plans from $3/mo with team seats." },
      { property: 'og:image', content: 'https://uptrack.app/og/compare-updown.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/compare/updown' },
    ],
    scripts: comparisonScripts('updown', "Updown.io"),
  }),
})

const data: ComparisonData = {
  competitor: "Updown.io",
  tagline: "Updown.io is a minimal uptime monitor billed with prepaid credits, with checks as often as every 15 seconds. Uptrack uses flat plans from $3/mo, with team seats and browser checks.",
  heroDescription: "These facts come from Updown.io's public pricing and docs pages as of October 2026. Plans change often, so check their site before you decide.",

  features: [
    { feature: "Pricing model", uptrack: "Flat monthly plans", competitor: "Prepaid credits (1 credit = 1 check)" },
    { feature: "Free plan", uptrack: "5 monitors at 30 s from Nuremberg", competitor: "Trial credits for new users" },
    { feature: "Fastest check interval", uptrack: "30 sec", competitor: "15 sec" },
    { feature: "Down confirmation", uptrack: "2-5 consecutive failed checks; several check locations must confirm", competitor: "Confirmed from at least 2 locations" },
    { feature: "Check locations", uptrack: MONITOR_LOCATIONS.compare.cell, competitor: "10", uptrackWins: MONITOR_LOCATIONS.compare.wins },
    { feature: "Status pages", uptrack: "5 on Pro, unlimited on Team", competitor: "Included" },
    { feature: "Team accounts", uptrack: "3 seats on Pro, 5 on Team, 15 on Business", competitor: "Single user (no team accounts)", uptrackWins: true },
  ],

  painPoints: [
    {
      title: "No team accounts",
      description: "Updown.io accounts are single-user, and its developer has said team accounts are not planned soon. Uptrack Pro includes 3 seats, Team 5, and Business 15.",
    },
    {
      title: "Cost grows with check frequency",
      description: "Updown.io bills per check. At a 30-second interval, one monitor runs about 86,400 checks every 30 days. Uptrack's price does not change with the interval: every plan checks every 30 seconds.",
    },
    {
      title: "Browser flows on the entry plan",
      description: "Uptrack Pro includes 5 Playwright browser monitors for $3/mo. Each run follows a flow such as login or checkout and saves a screenshot at each step, so you see which step broke.",
    },
  ],

  differentiators: [
    "Team features: seats for your whole team, escalation policies on Team and Business, and alert routing.",
    "Flat pricing: no credits to buy or track.",
    "Alert confirmation: Uptrack opens an incident only after 2-5 consecutive failed checks, and several check locations must confirm. One DNS timeout or CDN blip does not page you.",
    "Browser checks: Pro includes 5 Playwright monitors that run flows like login or checkout and save a screenshot at each step.",
    "REST API and an MCP server, so you can manage monitors from scripts or AI assistants such as Claude.",
  ],

  notForYou: [
    "If you want 15-second checks, Updown.io checks faster than our 30-second minimum.",
    "If you want pure pay-as-you-go pricing and only monitor 1-2 sites, Updown.io can cost less than any monthly plan.",
    "If you prefer a minimal interface, Updown.io is about as simple as monitoring gets.",
  ],
}
